
import React from 'react';
import { Upload, Database, FileText, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LinkedConnector, StandardizedConnection } from '@/types/connections';
import LinkedConnectorItem from './LinkedConnectorItem';

interface InitialOptionsViewProps {
  onFileOption: () => void;
  onDatabaseOption: () => void;
  linkedConnectors: StandardizedConnection[];
  onRemoveConnector: (id: string, connectionId: string) => Promise<void>;
  onClose: () => void;
  onPlaidReauth?: (connector: StandardizedConnection) => void;
}

const InitialOptionsView: React.FC<InitialOptionsViewProps> = ({
  onFileOption,
  onDatabaseOption,
  linkedConnectors,
  onRemoveConnector,
  onClose,
  onPlaidReauth
}) => {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2">Connect Your Data</h2>
        <p className="text-gray-600">Choose how you'd like to connect your business data</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div 
          className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer"
          onClick={onFileOption}
          data-testid="file-import-option"
        >
          <Upload className="mx-auto h-12 w-12 text-blue-500 mb-4" />
          <h3 className="text-lg font-medium mb-2">Upload Files</h3>
          <p className="text-sm text-gray-500 mb-4">
            Upload CSV, Excel, PDF, images, or other files directly
          </p>
          <Button variant="outline" className="w-full">
            <FileText size={16} className="mr-2" />
            Choose Files
            <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>

        <div 
          className="border-2 border-solid border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer"
          onClick={onDatabaseOption}
          data-testid="database-option"
        >
          <Database className="mx-auto h-12 w-12 text-green-500 mb-4" />
          <h3 className="text-lg font-medium mb-2">Connect Database</h3>
          <p className="text-sm text-gray-500 mb-4">
            Connect to Shopify, databases, or other data sources
          </p>
          <Button variant="outline" className="w-full">
            <Database size={16} className="mr-2" />
            Browse Connectors
            <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>
      </div>

      {linkedConnectors.length > 0 && (
        <div>
          <h3 className="text-lg font-medium mb-4">Connected Data Sources</h3>
          <div className="space-y-2">
            {linkedConnectors.map((connector) => (
              <LinkedConnectorItem
                key={connector.id}
                connector={connector}
                onRemove={() => onRemoveConnector(connector.id, connector.connectionId || connector.id)}
                onPlaidReauth={onPlaidReauth}
              />
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
};

export default InitialOptionsView;
